'use client'

import { getTurkishAudio } from '@/app/tts/actions'

// Клиентская часть озвучки 🔊 (серверная — app/tts/actions.ts).
// Один общий <audio> на всё приложение: новая фраза останавливает прежнюю.
//
// iOS Safari разрешает play() только из жеста пользователя, а URL озвучки
// приходит асинхронно (server action). Поэтому в самом клике общий элемент
// «разблокируется» — проигрывает пару миллисекунд тишины (unlockAudio), и
// дальше тот же элемент можно запускать уже после await. То же для
// speechSynthesis: пустая фраза в жесте.
//
// Нет URL (Google недоступен/ошибка) или mp3 не заиграл — говорит голос
// браузера (Web Speech API), кнопка звука не молчит.

export const SLOW_RATE = 0.75

type SpeakState = { id: string; status: 'loading' | 'playing' } | null
let state: SpeakState = null
const listeners = new Set<() => void>()
function setState(next: SpeakState) {
  state = next
  listeners.forEach((l) => l())
}
export function subscribeSpeak(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
export function getSpeakState() {
  return state
}

export function speakId(text: string, slow: boolean) {
  return `${slow ? 'slow' : 'norm'}|${text}`
}

// ── Уведомление «лимит озвучки исчерпан» (tts-limit-toast.tsx) ──
// Сервер ответил source: 'limit' — показываем один раз за загрузку страницы,
// дальше молча говорит голос браузера.
let ttsNotice = false
let ttsNoticeShownOnce = false
const noticeListeners = new Set<() => void>()
export function subscribeTtsNotice(cb: () => void) {
  noticeListeners.add(cb)
  return () => noticeListeners.delete(cb)
}
export function getTtsNotice() {
  return ttsNotice
}
export function dismissTtsNotice() {
  ttsNotice = false
  noticeListeners.forEach((l) => l())
}
function raiseTtsNotice() {
  if (ttsNoticeShownOnce) return
  ttsNoticeShownOnce = true
  ttsNotice = true
  noticeListeners.forEach((l) => l())
}

// ── Кэш URL на клиенте: повторное нажатие не ходит на сервер ──
const urlCache = new Map<string, Promise<string | null>>()
function resolveUrl(text: string, cacheOnly = false): Promise<string | null> {
  const key = text.replace(/\s+/g, ' ').trim()
  const ck = cacheOnly ? `cache|${key}` : key
  let p = urlCache.get(ck)
  if (!p) {
    p = getTurkishAudio(key, { cacheOnly }).then((r) => {
      if (r.source === 'limit') raiseTtsNotice()
      return r.url
    })
    p.then((url) => url === null && urlCache.delete(ck), () => urlCache.delete(ck))
    urlCache.set(ck, p)
  }
  return p
}

// Игры (components/games/speed-round.tsx): адрес озвучки следующей фразы
// узнаём заранее, чтобы после ответа она зазвучала сразу.
export function prefetchTurkish(text: string, { cacheOnly = false }: { cacheOnly?: boolean } = {}) {
  if (typeof window === 'undefined' || !text.trim()) return
  resolveUrl(text, cacheOnly).catch(() => {})
}

// ── Тишина для разблокировки: WAV 8 кГц, 20 мс нулей, собирается на лету ──
let silentWav: string | null = null
// Экспорт: запись себя (components/record-self.tsx) разблокирует свой <audio> тем же способом.
export function silentWavUrl() {
  if (silentWav) return silentWav
  const samples = 160
  const buf = new ArrayBuffer(44 + samples)
  const v = new DataView(buf)
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)))
  str(0, 'RIFF')
  v.setUint32(4, 36 + samples, true)
  str(8, 'WAVE')
  str(12, 'fmt ')
  v.setUint32(16, 16, true)
  v.setUint16(20, 1, true) // PCM
  v.setUint16(22, 1, true) // моно
  v.setUint32(24, 8000, true)
  v.setUint32(28, 8000, true)
  v.setUint16(32, 1, true)
  v.setUint16(34, 8, true) // 8 бит
  str(36, 'data')
  v.setUint32(40, samples, true)
  for (let i = 0; i < samples; i++) v.setUint8(44 + i, 128) // тишина для 8-бит PCM
  let bin = ''
  new Uint8Array(buf).forEach((b) => (bin += String.fromCharCode(b)))
  silentWav = `data:audio/wav;base64,${btoa(bin)}`
  return silentWav
}

let audioEl: HTMLAudioElement | null = null
function getAudio() {
  if (!audioEl) audioEl = new Audio()
  return audioEl
}

// Вызывать синхронно в обработчике клика/тапа.
export function unlockAudio() {
  if (typeof window === 'undefined') return
  const a = getAudio()
  a.pause()
  a.src = silentWavUrl()
  a.play().catch(() => {})
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel()
    window.speechSynthesis.speak(new SpeechSynthesisUtterance(''))
  }
}

export function stopSpeaking() {
  audioEl?.pause()
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel()
  setState(null)
}

function turkishVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis.getVoices()
  return voices.find((v) => v.lang.toLowerCase().replace('_', '-').startsWith('tr')) ?? null
}

function speakWithBrowser(text: string, slow: boolean, id: string): Promise<void> {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) return resolve()
    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'tr-TR'
    const voice = turkishVoice()
    if (voice) u.voice = voice
    u.rate = slow ? SLOW_RATE : 0.95
    u.onstart = () => state?.id === id && setState({ id, status: 'playing' })
    u.onend = u.onerror = () => resolve()
    window.speechSynthesis.cancel()
    window.speechSynthesis.speak(u)
  })
}

let playSeq = 0

// Сказать фразу. Из обработчика клика — вызывать напрямую (разблокировка
// внутри); для автоозвучки — после того как клик уже вызвал unlockAudio().
// audioUrl — готовый mp3 (озвучка урока, известная странице заранее): тогда
// сервер не спрашиваем — так 🔊 работает и у гостя без сессии (публичный
// разговорник /travel/phrases, server action там отказал бы).
export async function speakTurkish(
  text: string,
  {
    slow = false,
    fromGesture = true,
    audioUrl,
    cacheOnly = false,
  }: {
    slow?: boolean
    fromGesture?: boolean
    audioUrl?: string | null
    // Только заранее озвученное, без синтеза и без голоса браузера: игры молчат,
    // если фразы ещё нет в кэше, а не читают её роботом.
    cacheOnly?: boolean
  } = {},
) {
  if (typeof window === 'undefined' || !text.trim()) return
  const id = speakId(text, slow)
  const seq = ++playSeq
  if (fromGesture) unlockAudio()
  setState({ id, status: 'loading' })

  let url: string | null = audioUrl ?? null
  if (!url) {
    try {
      url = await resolveUrl(text, cacheOnly)
    } catch {
      url = null
    }
  }
  if (seq !== playSeq) return // пока ждали — нажали другую кнопку

  let played = false
  if (url) {
    const a = getAudio()
    try {
      a.pause()
      a.src = url
      a.defaultPlaybackRate = slow ? SLOW_RATE : 1
      a.playbackRate = slow ? SLOW_RATE : 1
      a.preservesPitch = true
      await a.play()
      played = true
      setState({ id, status: 'playing' })
      await new Promise<void>((resolve) => {
        const done = () => {
          a.removeEventListener('ended', done)
          a.removeEventListener('pause', done)
          a.removeEventListener('error', done)
          resolve()
        }
        a.addEventListener('ended', done)
        a.addEventListener('pause', done)
        a.addEventListener('error', done)
      })
    } catch {
      played = false
    }
  }

  if (!played && seq === playSeq && !cacheOnly) await speakWithBrowser(text, slow, id)
  if (seq === playSeq) setState(null)
}
